import * as posActions from "../../lib/posActions";
import * as routeHelpers from "../../lib/routeHelpers";
import * as searchParamNames from "../../constants/searchParamNames";
import AllergyWarningModal from "../ComboCustomization/AllergyWarningModal";
import cn from "classnames";
import ComboSubstitutionsModal from "../ComboSubstitutionsModal";
import CustomizationList from "../CustomizationList";
import Drawer from "../Drawer";
import getInitialModSelections from "../../lib/getInitialModSelections";
import ItemEditor from "../ItemEditor";
import logger from "../../lib/sentryLogger";
import OutlineButton from "../OutlineButton";
import QuantityControl from "../QuantityControl";
import React, {useState, useEffect, useMemo} from "react";
import RetinaImage from "../RetinaImage";
import SplitDrawerContent from "../SplitDrawerContent";
import styles from "./ItemCustomization.module.css";
import ToggleButton from "../ToggleButton/ToggleButton";
import {API, Lib} from "habit-core";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {v4 as uuid4} from "uuid";

const strings = {
    addToOrder: "Add To Order",
    updateOrder: "Update Order",
    cancel: "Cancel",
    addOnsTitle: "Suggested Add-Ons",
    makeItACombo: "Make it a Combo",
};

type Params = {
    categoryId: string;
    itemId: string;
};

function getUnitPriceCents(
    menuItemPriceCents: API.models.USDCents,
    modifierSelections: API.models.ModifierSelectionDict,
    menuItemModifiers: {[modifierId: string]: API.models.MenuItemModifier},
    allPreps: {byId: {[modifierId: string]: Lib.selectors.ItemModifierData}},
): API.models.USDCents {
    let perUnitPriceCents = menuItemPriceCents;
    Object.keys(modifierSelections).forEach((modId) => {
        const mod = menuItemModifiers[modId];
        if (mod) {
            const [sel] = mod.selections.filter(
                (x) => x.id === modifierSelections[modId]?.selectionId,
            );

            if (sel && sel.priceCents) {
                perUnitPriceCents +=
                    sel.priceCents * modifierSelections[modId]?.quantity;
            }
        } else {
            const allPrepsMod = allPreps.byId[modId];
            const allPrepModSelection = allPrepsMod?.selections.find(
                (selection) =>
                    selection.id === modifierSelections[modId]?.selectionId,
            );

            if (allPrepModSelection && allPrepModSelection.priceCents) {
                perUnitPriceCents +=
                    allPrepModSelection.priceCents *
                    modifierSelections[modId]?.quantity;
            }
        }
    });

    return perUnitPriceCents;
}

export default function ItemCustomization() {
    const navigate = useNavigate();
    const {categoryId, itemId} = useParams<Params>();
    const [searchParams] = useSearchParams();
    const icid = searchParams.get(searchParamNames.ITEM_CUSTOMIZATION_ID);
    const menuId = useAppSelector((state) => state.currentOrder.menuId);
    useEffect(() => {
        if (!menuId || !categoryId || !itemId) {
            logger.warn(
                "Attempting to customize item. Invalid menu id, category id, and/or item id",
                {
                    menuId: menuId,
                    categoryId: categoryId,
                    itemId: itemId,
                },
            );
            navigate(routeHelpers.menu(), {replace: true});
        }
    }, []);

    const itemsById = useAppSelector((state) => state.items.byId);
    const item = itemId ? itemsById[itemId] : null;

    useEffect(() => {
        if (!item) {
            logger.warn("Attempting to customize item. No item found", {
                itemId: itemId,
            });
            navigate(routeHelpers.menu(), {replace: true});
        }
    }, []);

    const menuItemsByMenuId = useAppSelector(
        (state) => state.menuItems.byMenuId,
    );
    const menuItem = menuId && item ? menuItemsByMenuId[menuId][item.id] : null;
    const menuItemModifiersByMenuId = useAppSelector(
        (state) => state.menuItemModifiers.byMenuId,
    );
    const allPrepsModifiersById = useAppSelector((state) => state.pos.allPreps);
    const menuItemModifiers =
        menuId && item ? menuItemModifiersByMenuId[menuId][item.id] : {};

    const getItemComboSubstitutions = useMemo(
        Lib.selectors.createGetItemComboSubstitutions,
        [],
    );
    const matchedCombos = useAppSelector((state) =>
        item ? getItemComboSubstitutions(state, item.id) : null,
    );
    const [comboSubstitutionsModalVisible, setComboSubstitutionsModalVisible] =
        useState(false);

    const addOns = useAppSelector(
        (state) => state.pos.addOns.byId[item?.id ?? ""],
    );
    const unfilteredAddOnsInfo = addOns?.map((addOnId) => {
        const addOnMenuItem = menuItemsByMenuId[menuId ?? ""][addOnId];
        const addOnItem = itemsById[addOnId];
        if (addOnMenuItem) {
            return {
                itemId: addOnMenuItem.itemId,
                name: addOnItem.name,
                priceCents: addOnMenuItem.priceCents,
            };
        } else {
            return null;
        }
    });
    const addOnsInfo: {
        itemId: string;
        name: string;
        priceCents: API.models.USDCents;
    }[] = unfilteredAddOnsInfo?.filter(
        (addOnInfo): addOnInfo is Exclude<typeof addOnInfo, null> =>
            addOnInfo !== null,
    );

    const [selectedAddOnIds, setSelectedAddOnIds] = useState<string[]>([]);

    function renderAddOns() {
        if ((addOnsInfo?.length ?? 0) === 0) {
            return;
        }

        return (
            <div className={styles.addOnsContainer}>
                {strings.addOnsTitle}
                <div className={styles.addOnsOptionsContainer}>
                    {addOnsInfo.map((addOnInfo, i) => (
                        <ToggleButton
                            key={"add-on-" + i}
                            className={styles.addOnsOption}
                            checked={selectedAddOnIds.includes(
                                addOnInfo.itemId,
                            )}
                            label={addOnInfo.name}
                            label2={`+ ${Lib.currency.centsToDollarString(
                                addOnInfo.priceCents,
                            )}`}
                            onChange={(checked) => {
                                if (checked) {
                                    setSelectedAddOnIds([
                                        ...selectedAddOnIds,
                                        addOnInfo.itemId,
                                    ]);
                                } else {
                                    setSelectedAddOnIds([
                                        ...selectedAddOnIds.filter(
                                            (addOnId) =>
                                                addOnId !== addOnInfo.itemId,
                                        ),
                                    ]);
                                }
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    const initialCustomization = useAppSelector((state) => {
        if (!icid) {
            return null;
        }

        return state.customizations.items.byInternalId[icid] ?? null;
    });

    const onClickCombo = (categoryId: string, comboId: string) => {
        navigate(routeHelpers.combo(categoryId, comboId), {
            state: {
                comboUpgrade: {
                    entreeComboItem: {
                        itemId: itemId,
                        modifierSelections: modSelections,
                    },
                    existingItemCustomizationId:
                        initialCustomization?.customizationId,
                },
            },
        });
    };

    const showDigitalOnly = useAppSelector(
        (state) =>
            !!state.pos.currentOrder.orderNumber &&
            (state.pos.station.mode === "front_of_house_1" ||
                state.pos.station.mode === "front_of_house_2"),
    ); // order is pending

    const dispatch = useAppDispatch();
    const [quantity, setQuantity] = useState(
        initialCustomization ? initialCustomization.quantity : 1,
    );

    const posAllPrepsMods = useAppSelector((state) => state.pos.allPreps);
    const allergyWarningShown = useAppSelector(
        (state) => state.pos.currentOrder.allergyWarningShown,
    );
    const [showAllergyWarning, setShowAllergyWarning] = useState(false);

    const onClose = () =>
        navigate(
            categoryId
                ? routeHelpers.category(categoryId)
                : routeHelpers.menu(),
        );

    const [modSelections, setModSelections] =
        useState<API.models.ModifierSelectionDict>(
            getInitialModSelections(
                itemsById,
                item?.id ?? "",
                menuItemsByMenuId,
                menuId ?? "",
                menuItemModifiersByMenuId,
                initialCustomization,
                true,
                !showDigitalOnly,
            ),
        );

    if (!categoryId || !itemId || !menuId || !item || !menuItem) {
        return null;
    }

    const updateModSelection = (
        modifierId: string,
        selectionId: string,
        quantity: number,
        isDefault: boolean,
        isRequired: boolean,
    ) => {
        setModSelections((prevState) => {
            if (
                prevState[modifierId]?.selectionId === selectionId &&
                prevState[modifierId]?.quantity === quantity
            ) {
                return prevState;
            }

            const nextModSelections = {...prevState};
            if (isDefault && !isRequired) {
                delete nextModSelections[modifierId];
            } else {
                nextModSelections[modifierId] = {selectionId, quantity};
            }

            // TODO: #56 - Do we need dirty tracking?
            return nextModSelections;
        });
    };

    const onSave = () => {
        if (selectedAddOnIds.length !== 0) {
            const itemCustomizationsToAdd = selectedAddOnIds.map((addOnId) => {
                return {
                    customizationId: uuid4(),
                    itemId: addOnId,
                    modifierSelections: {},
                    quantity: quantity,
                };
            });
            dispatch(
                posActions.addItems(
                    itemCustomizationsToAdd,
                    [],
                    false,
                    !showDigitalOnly,
                ),
            );
        }

        if (initialCustomization) {
            dispatch(
                posActions.modifyCustomization(
                    initialCustomization.customizationId,
                    quantity,
                    modSelections,
                ),
            );
        } else {
            dispatch(
                posActions.addItem(
                    item.id,
                    quantity,
                    modSelections,
                    undefined,
                    !showDigitalOnly,
                ),
            );

            if (
                !allergyWarningShown &&
                Object.keys(modSelections).find(
                    (modId) => posAllPrepsMods.byId[modId]?.isAllergy,
                )
            ) {
                dispatch(posActions.setAllergyWarningShown(true));
                setShowAllergyWarning(true);
                return;
            }
        }
        onClose();
    };

    const baseUnitPriceCents = getUnitPriceCents(
        menuItem.priceCents,
        {},
        menuItemModifiers,
        allPrepsModifiersById,
    );
    const totalPriceCents =
        getUnitPriceCents(
            menuItem.priceCents,
            modSelections,
            menuItemModifiers,
            allPrepsModifiersById,
        ) * quantity;

    return (
        <>
            <Drawer
                size="large"
                onClose={onClose}
                closeButtonLabel={strings.cancel}>
                <SplitDrawerContent
                    className={styles.content}
                    left={
                        <ItemEditor
                            itemId={item.id}
                            itemKey={item.id}
                            menuId={menuId}
                            modifierSelections={modSelections}
                            onChangeModifier={updateModSelection}
                            excludeDigitalOnly={!showDigitalOnly}
                        />
                    }
                    right={
                        <>
                            <div className={styles.titleAndPrice}>
                                {item.isolatedImages352.length > 0 && (
                                    <div className={styles.imageContainer}>
                                        <RetinaImage
                                            retinaImages={
                                                item.isolatedImages352
                                            }
                                            className={styles.image}
                                        />
                                    </div>
                                )}
                                <div
                                    className={cn(
                                        styles.title,
                                        item.isolatedImages352.length > 0
                                            ? styles.titleMargin
                                            : null,
                                    )}>
                                    {item.name}
                                </div>
                                <div className={styles.price}>
                                    {Lib.currency.centsToDollarString(
                                        baseUnitPriceCents,
                                    )}
                                </div>
                            </div>

                            <div className={styles.divider} />
                            <div className={styles.rightCustomizations}>
                                <CustomizationList
                                    modifierSelections={modSelections}
                                    itemId={item.id}
                                    onChangeModifier={updateModSelection}
                                    editable={true}
                                />

                                <div className={styles.spacer} />
                            </div>
                            <div className={styles.divider} />
                            {renderAddOns()}
                            {matchedCombos && matchedCombos.length ? (
                                <OutlineButton
                                    className={styles.makeItAComboButton}
                                    label={strings.makeItACombo}
                                    labelClassName={styles.makeItAComboLabel}
                                    mode="blue"
                                    onClick={() => {
                                        if (matchedCombos.length > 1) {
                                            setComboSubstitutionsModalVisible(
                                                true,
                                            );
                                        } else {
                                            onClickCombo(
                                                matchedCombos[0].categoryIds[0],
                                                matchedCombos[0].id,
                                            );
                                        }
                                    }}
                                />
                            ) : null}
                            <div className={styles.divider} />
                            <div className={styles.qtyAddToOrder}>
                                <QuantityControl
                                    quantity={quantity}
                                    setQuantity={setQuantity}
                                />

                                <OutlineButton
                                    label={Lib.currency.centsToDollarString(
                                        totalPriceCents,
                                    )}
                                    label2={
                                        initialCustomization
                                            ? strings.updateOrder
                                            : strings.addToOrder
                                    }
                                    onClick={onSave}
                                    className={styles.fullWidth}
                                />
                            </div>
                        </>
                    }
                />
            </Drawer>
            {showAllergyWarning ? (
                <AllergyWarningModal onClose={onClose} />
            ) : null}
            {comboSubstitutionsModalVisible && matchedCombos && (
                <ComboSubstitutionsModal
                    combos={matchedCombos}
                    replacedEntreeItem={{
                        itemId: itemId,
                        modifierSelections: modSelections,
                    }}
                    onClickCombo={onClickCombo}
                    onClose={() => setComboSubstitutionsModalVisible(false)}
                />
            )}
        </>
    );
}
